import { ReportTemplate } from "src/types";
import { useUser } from "src/hooks/useUser";
import { IncomeProvider } from "src/hooks/useData";
import {
  updateReportTemplate,
  createReportTemplate,
} from "src/services/reportTemplate";
import TemplateOptions from "./TemplateOptions";
import TemplateOrder from "./TemplateOrder";
import Input from "../Inputs/Input";
import sampleClient from "./sampleClient";
import { printReport } from "src/utils";
import { useState, useEffect } from "react";
import Button from "../Inputs/Button";
import ModalInput from "../Inputs/ModalInput";
import { useWindowSize } from "src/hooks/useWindowSize";

const ReportTemplateSettings = ({
  template,
  clientData,
  scenarioData,
  onPrintStart,
  onPrintEnd,
  onClose: onCloseOriginal,
  customSetTemplate,
}: {
  template: ReportTemplate;
  clientData?: any;
  scenarioData?: any;
  onPrintStart?: () => void;
  onPrintEnd?: () => void;
  onClose?: () => void;
  customSetTemplate?: (template: Partial<ReportTemplate>) => void;
}) => {
  const { user, updateUser } = useUser();
  const client = clientData || sampleClient(user);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [originalTemplate, setOriginalTemplate] = useState(template);
  const [w, _] = useWindowSize();

  const isCustomTemplate = template?.id === "custom";

  // Don't render if template is null
  if (!template) {
    return null;
  }

  const onClose = () => {
    onCloseOriginal?.();
    setOriginalTemplate(template);
  };

  // Track changes when template updates
  useEffect(() => {
    setOriginalTemplate(template);
    setHasChanges(false);
  }, [template.id]);

  useEffect(() => {
    const templateChanged =
      JSON.stringify(template) !== JSON.stringify(originalTemplate);
    setHasChanges(templateChanged);
  }, [template, originalTemplate]);

  const updateTemplate = (newTemplate: Partial<ReportTemplate>) => {
    if (isCustomTemplate && customSetTemplate) {
      // For custom templates, use the custom setTemplate function
      customSetTemplate(newTemplate);
      return;
    }
    if (!isCustomTemplate) {
      updateUser({
        reportTemplates: user?.reportTemplates.map((t) =>
          t.id === template.id ? { ...t, ...newTemplate } : t,
        ),
      });
      updateReportTemplate(template.id, {
        ...template,
        ...newTemplate,
      }).then(() => {});
    }
  };

  const handlePrint = async () => {
    if (clientData && scenarioData) {
      onPrintStart?.();
      try {
        const url = await printReport(
          clientData.id,
          scenarioData.id,
          template.settings,
        );
        window.open(url, "_blank");
      } finally {
        onPrintEnd?.();
      }
    }
  };

  const handleUpdateAndPrint = async () => {
    // Save current changes first
    await updateReportTemplate(template.id, template);
    // Reset changes state
    setOriginalTemplate(template);
    setHasChanges(false);
  };

  const handleSaveAndPrint = async () => {
    if (!newTemplateName.trim()) {
      alert("Please enter a name for the new template");
      return;
    }
    setOriginalTemplate(template);
    setHasChanges(false);
    try {
      // Create new template
      const response = await createReportTemplate({
        name: newTemplateName,
        settings: template.settings,
      });

      if (response.ok) {
        const newTemplate = await response.json();
        // Update user's template list
        updateUser({
          reportTemplates: [...(user?.reportTemplates || []), newTemplate],
        });

        // Reset form
        setShowSaveModal(false);
        setNewTemplateName("");
      }
    } catch (error) {
      console.error("Error creating new template:", error);
      alert("Error creating new template. Please try again.");
    }
  };

  return (
    <div>
      <IncomeProvider data={client as any} setLocal={() => {}}>
        <div className="w-full flex justify-end">
          {/* Print Buttons */}
          <div className="mb-5 flex gap-3 flex-wrap mt-[-40px]">
            {clientData && scenarioData && (
              <div className="w-[120px]">
                <Button type="primary" onClick={handlePrint}>
                  Print
                </Button>
              </div>
            )}

            {clientData && scenarioData && isCustomTemplate && (
              <div className="w-[200px]">
                <Button type="secondary" onClick={() => setShowSaveModal(true)}>
                  Save as new template
                </Button>
              </div>
            )}

            {clientData && scenarioData && !isCustomTemplate && hasChanges && (
              <>
                <div className="max-w-[550px]">
                  <Button type="secondary" onClick={handleUpdateAndPrint}>
                    Save changes to "{template.name}"
                  </Button>
                </div>
                <div className="w-[200px]">
                  <Button
                    type="secondary"
                    onClick={() => setShowSaveModal(true)}
                  >
                    Save a new template
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-6 w-full">
          <div
            className="bg-gray-50 border rounded-lg p-4"
            style={{
              flex: 1,
              minWidth: 220,
              maxWidth: 600,
              maxHeight: 600,
            }}
          >
            <TemplateOptions
              template={template}
              setTemplate={updateTemplate}
              onClose={onClose}
            />
          </div>
          <div
            className="bg-gray-50 border rounded-lg p-4"
            style={{
              flex: 1,
              minWidth: 300,
              maxWidth: 600,
              // height: "65vh",
              maxHeight: 600,
            }}
          >
            <TemplateOrder template={template} setTemplate={updateTemplate} />
          </div>
          <div
            className="max-h-[650px] bg-gray-50 border rounded-lg p-4 px-6 overflow-auto"
            style={{
              flex: "0 1 auto",
              minWidth: "300px",
              maxHeight: 600,
              width: w * 0.35,
            }}
          >
            <div className="text-2xl font-semibold mb-4">Preview</div>
            {template.settings.map((page) => {
              return (
                <div
                  className={`p-4 bg-white overflow-hidden relative border mb-7`}
                >
                  <div>
                    <img
                      src={`/report-preview/${page.name}.png`}
                      alt=""
                      style={{
                        width: w * 0.6,
                        height: "auto",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </IncomeProvider>

      <ModalInput
        isOpen={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setNewTemplateName("");
        }}
        onConfirm={handleSaveAndPrint}
      >
        <div className="text-lg font-semibold mb-4">
          {isCustomTemplate ? "Save New Template" : "Save New Template"}
        </div>
        <Input
          vertical
          label="Template Name"
          width="!w-[200px]"
          value={newTemplateName}
          setValue={setNewTemplateName}
          onKeyDown={(e: any) => {
            if (e.key === "Enter") {
              handleSaveAndPrint();
            }
          }}
        />
      </ModalInput>
    </div>
  );
};

export default ReportTemplateSettings;
// {page.name !== "cover" && (
//                         <Header
//                           visible
//                           page={page.name}
//                           client={client as any}
//                           scenario={scenario as any}
//                         />
//                       )}
//                       {page.name !== "cover" && (
//                         <Footer
//                           visible
//                           client={client as any}
//                           scenario={
//                             {
//                               ...client.liveSettings,
//                               ...client.scenarios[0],
//                             } as any
//                           }
//                         />
//                       )}
//                       <Page
//                         scenario={
//                           {
//                             ...client.liveSettings,
//                             ...client.scenarios[0],
//                           } as any
//                         }
//                         client={
//                           {
//                             ...client,
//                             userdata: user?.info,
//                           } as any
//                         }
//                       />
